/**
 * PIM chat reducer (BM-6229). State is keyed by channelSetupId so messages and
 * unread counts for each setup are tracked independently. All updates are immutable.
 */
import t from './PIMChat.type';

const initialState = {
  accessibleSetups: [],
  selectedSetupId: null,
  messagesBySetup: {},
  unreadCounts: {},
  loading: false,
  error: null,
};

/**
 * @param {object} state - current slice
 * @param {{ type: string, payload: * }} action
 * @returns {object} next slice
 */
export default function PIMChatReducer(state = initialState, action = {}) {
  switch (action.type) {
    case t.SET_ACCESSIBLE_SETUPS:
      return { ...state, accessibleSetups: action.payload || [] };

    case t.SET_SELECTED_SETUP:
      return { ...state, selectedSetupId: action.payload };

    case t.SET_MESSAGES:
      return {
        ...state,
        messagesBySetup: {
          ...state.messagesBySetup,
          [action.payload.channelSetupId]: action.payload.messages || [],
        },
      };

    case t.APPEND_MESSAGE: {
      const { channelSetupId, message } = action.payload;
      const existing = state.messagesBySetup[channelSetupId] || [];
      if (existing.some((m) => m.serial === message.serial)) return state;
      return {
        ...state,
        messagesBySetup: {
          ...state.messagesBySetup,
          [channelSetupId]: [...existing, message],
        },
      };
    }

    case t.UPDATE_MESSAGE: {
      const { channelSetupId, serial, message } = action.payload;
      const existing = state.messagesBySetup[channelSetupId] || [];
      return {
        ...state,
        messagesBySetup: {
          ...state.messagesBySetup,
          [channelSetupId]: existing.map((m) => (m.serial === serial ? { ...m, message, isEdited: true } : m)),
        },
      };
    }

    case t.SOFT_DELETE_MESSAGE: {
      const { channelSetupId, serial } = action.payload;
      const existing = state.messagesBySetup[channelSetupId] || [];
      return {
        ...state,
        messagesBySetup: {
          ...state.messagesBySetup,
          [channelSetupId]: existing.map((m) => (m.serial === serial ? { ...m, isDeleted: true } : m)),
        },
      };
    }

    case t.SET_UNREAD_COUNTS: {
      const counts = {};
      const payload = Array.isArray(action.payload) ? action.payload : [];
      payload.forEach((row) => {
        counts[row.channelSetupId] = row.count;
      });
      return { ...state, unreadCounts: counts };
    }

    case t.INCREMENT_UNREAD: {
      const id = action.payload;
      return {
        ...state,
        unreadCounts: { ...state.unreadCounts, [id]: (state.unreadCounts[id] || 0) + 1 },
      };
    }

    case t.CLEAR_UNREAD: {
      const next = { ...state.unreadCounts };
      delete next[action.payload];
      return { ...state, unreadCounts: next };
    }

    case t.SET_LOADING:
      return { ...state, loading: action.payload };

    case t.SET_ERROR:
      return { ...state, error: action.payload };

    case t.CLEAR_REDUCER:
      return { ...initialState };

    default:
      return state;
  }
}
